import React, { useState, useEffect } from "react";
import "./BlogSection.css";
import { Link } from "react-router-dom";

const getBlogsFromStorage = () => {
  // Default hardcoded blogs (first 3)
  const defaultBlogs = [
    {
      id: 1,
      title: "Top 10 Ways to Improve Office Productivity",
      desc: "Learn expert-backed methods to boost employee efficiency.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80",
      slug: "office-productivity",
    },
    {
      id: 2,
      title: "Why Corporate Housekeeping Matters in 2025",
      desc: "Explore the importance of housekeeping in workplaces.",
      image: "https://images.unsplash.com/photo-1520880867055-1e30d1cb001c?auto=format&fit=crop&w=1600&q=80",
      slug: "corporate-housekeeping",
    },
    {
      id: 3,
      title: "How Industrial Cleaning Ensures Workplace Safety",
      desc: "Discover how professional industrial cleaning reduces risks and prevents workplace hazards.",
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80",
      slug: "industrial-cleaning-safety",
    },
  ];

  const storedBlogs = localStorage.getItem('adminBlogs');
  if (storedBlogs) {
    const adminBlogs = JSON.parse(storedBlogs);
    // PRODUCTION MODE: Only show published blogs
    // TESTING MODE: Uncomment the line below to show draft blogs for testing
    // const adminBlogsToShow = adminBlogs.filter(blog => blog.status === 'published' || blog.status === 'draft');
    const adminBlogsToShow = adminBlogs
      .filter(blog => blog.status === 'published') // PRODUCTION: Only published blogs
      .map(blog => ({
        id: blog.id + 1000, // Add offset to avoid ID conflicts
        title: blog.title,
        desc: blog.content,
        image: blog.image || "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80",
        slug: blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      }));

    // Combine default blogs with admin blogs
    return [...defaultBlogs, ...adminBlogsToShow];
  }

  // Return only default blogs if no stored data
  return defaultBlogs;
};

export default function BlogSection() {
  const [blogs, setBlogs] = useState(getBlogsFromStorage);
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 18; // 6 rows * 3 columns

  useEffect(() => {
    const handleStorageChange = () => {
      setBlogs(getBlogsFromStorage());
      setCurrentPage(1); // Reset to first page on data change
    };

    const handleBlogsUpdate = () => {
      setBlogs(getBlogsFromStorage());
      setCurrentPage(1); // Reset to first page on data change
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('blogsUpdated', handleBlogsUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('blogsUpdated', handleBlogsUpdate);
    };
  }, []);

  const totalPages = Math.ceil(blogs.length / blogsPerPage);
  const startIndex = (currentPage - 1) * blogsPerPage;
  const currentBlogs = blogs.slice(startIndex, startIndex + blogsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <section className="blog-section">
      <div className="blog-header">
        <span className="blog-tag">Latest Articles</span>
        <h2>Our Blog & Insights</h2>
        <p>Explore our latest articles and industry insights.</p>
      </div>

      <div className="blog-grid">
        {currentBlogs.map((blog) => (
          <div key={blog.id} className="blog-card">
            <div className="blog-image">
              <img src={blog.image} alt={blog.title} loading="lazy" />
            </div>

            <div className="blog-content">
              <h3>{blog.title}</h3>
              <p>{blog.desc}</p>

              {/* UPDATED Read More Link */}
              <Link to={`/blogs/${blog.slug}`} className="blog-link">
                Read More →
              </Link>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
            >
              {index + 1}
            </button>
          ))}

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
